import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowUpRight, ShieldCheck, Eye, ScrollText, Globe2, Sparkles, BookOpen, AlertTriangle, Building } from 'lucide-react';
import SEO from '../components/SEO';
import Img from '../components/Img';
import { EDITORIAL_HEROES } from '../data/heroImages';

const PILLARS = [
  { icon: ShieldCheck, t: 'Trust', d: 'Four-tier verification system grounded in documentation — not paid placement. Buyers see the source of every claim.' },
  { icon: Eye, t: 'Transparency', d: 'Brand associations categorised as Verified, Publicly Reported, Unverified, or Historical. No marketplace fog.' },
  { icon: ScrollText, t: 'Verification', d: 'Business registration, audit reports, factory media — submitted, reviewed, and surfaced as structured intelligence.' },
  { icon: Building, t: 'Supplier Intelligence', d: 'Intelligence-brief supplier profiles rendered like editorial features — capacity, lead time, exports, and brand context in one frame.' },
  { icon: Sparkles, t: 'Modern UX', d: 'Sharp, fast, monochrome. Mobile-first. Built like a fashion publication, not a B2B catalogue.' },
  { icon: BookOpen, t: 'Editorial Discovery', d: 'Trends, intelligence briefs, and country-by-country sourcing context — curated weekly by editorial intelligence.' },
  { icon: Globe2, t: 'AI-Assisted Sourcing', d: 'Upload a product photo, surface visually matched factories. Built today for image-based sourcing — extending to vector search.' },
  { icon: AlertTriangle, t: 'Supplier Accountability', d: 'Featured placement is sponsored and labelled. Trust score and verification are independent — credibility is non-negotiable.' },
];

const WhyGiwahs = () => {
  return (
    <div data-testid="why-giwahs-page" className="surface-obsidian text-platinum">
      <SEO
        title="Why GIWAHS — The Modern Sourcing Intelligence Platform"
        description="GIWAHS combines luxury fashion editorial, modern AI platform, and premium sourcing intelligence — built on trust, transparency, verification, and supplier accountability."
      />
      {/* HERO */}
      <section className="relative overflow-hidden border-b border-black bg-[#0A0A0A] text-white grain-overlay">
        <div className="absolute inset-0">
          <Img src={EDITORIAL_HEROES.whyGiwahs.src} alt={EDITORIAL_HEROES.whyGiwahs.alt} variant="hero" priority className="h-full w-full object-cover opacity-40" />
          <div className="absolute inset-0 hero-overlay" />
        </div>
        <div className="relative mx-auto max-w-[1440px] px-5 py-16 md:px-8 md:py-28">
          <span className="label-overline" style={{ color: '#C8A96B' }}>/ Why Giwahs</span>
          <h1 className="mt-6 font-display tracking-tighter" style={{ fontWeight: 700, fontSize: 'clamp(2.5rem, 9vw, 8rem)', lineHeight: 0.9 }}>
            The modern<br />sourcing<br /><em className="giwahs-gold-emphasis" style={{ fontStyle: 'italic' }}>intelligence</em><br />platform.
          </h1>
        </div>
      </section>

      {/* OPENING STATEMENT */}
      <section className="border-b border-hairline surface-obsidian-2">
        <div className="mx-auto max-w-[1100px] px-5 py-16 md:px-8 md:py-24">
          <span className="label-overline">/ The thesis</span>
          <p className="mt-6 font-display tracking-tighter text-3xl md:text-5xl leading-tight" style={{ fontWeight: 500 }}>
            "Finding the right manufacturing partner isn't always straightforward."
          </p>
          <p className="mt-8 max-w-2xl text-base leading-relaxed md:text-lg giwahs-thesis-body">
            Traditional B2B marketplaces optimise for transactions. GIWAHS optimises for clarity — and does the hard work of organising and assessing supplier information, so you can focus on building your business with confidence.
          </p>
        </div>
      </section>

      {/* PILLARS */}
      <section className="border-b border-hairline surface-obsidian">
        <div className="mx-auto max-w-[1440px] px-5 py-16 md:px-8 md:py-24">
          <span className="label-overline">/ Eight pillars</span>
          <h2 className="mt-3 font-display text-4xl tracking-tighter md:text-6xl" style={{ fontWeight: 600 }}>
            What we stand for.
          </h2>
          <div className="mt-12 grid gap-px bg-black sm:grid-cols-2 lg:grid-cols-4">
            {PILLARS.map((p, i) => (
              <div key={p.t} className="giwahs-pillar-card p-6" data-testid={`pillar-${i}`}>
                <p.icon size={20} strokeWidth={1.5} className="text-[#C8A64A]" />
                <div className="giwahs-pillar-heading mt-5 text-xl tracking-tight md:text-2xl">{p.t}</div>
                <p className="mt-3 text-sm leading-relaxed text-platinum-2">{p.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CONTRAST: MARKETPLACE VS INTELLIGENCE */}
      <section className="border-b border-black bg-[#0A0A0A] text-white grain-overlay">
        <div className="mx-auto max-w-[1440px] px-5 py-16 md:px-8 md:py-24">
          <span className="label-overline text-neutral-400">/ Marketplace vs Intelligence</span>
          <h2 className="mt-3 font-display text-4xl tracking-tighter md:text-6xl" style={{ fontWeight: 600 }}>How we differ.</h2>
          <div className="mt-12 grid gap-px bg-neutral-800 md:grid-cols-2">
            <div className="bg-[#0A0A0A] p-7 md:p-10">
              <span className="label-overline text-neutral-500">Traditional B2B marketplace</span>
              <ul className="mt-6 space-y-3 text-sm leading-relaxed text-neutral-400">
                <li>· Rankings often shaped by paid placement</li>
                <li>· Verification signals vary between platforms</li>
                <li>· Supplier types are rarely classified</li>
                <li>· Brand associations are hard to confirm</li>
                <li>· Optimised for transactions</li>
              </ul>
            </div>
            <div className="bg-[#0A0A0A] p-7 md:p-10">
              <span className="label-overline">Giwahs intelligence</span>
              <ul className="mt-6 space-y-3 text-sm leading-relaxed text-neutral-200">
                <li>· Trust-sorted directory, independent of paid placement</li>
                <li>· Four-tier badge system tied to documentation</li>
                <li>· Supplier type clearly classified (Direct / OEM / ODM / Trading / Agent / Wholesaler)</li>
                <li>· Brand associations categorised: Verified / Publicly Reported / Unverified / Historical</li>
                <li>· Editorial, intelligence-brief UX</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="surface-obsidian text-platinum">
        <div className="mx-auto max-w-[1440px] px-5 py-16 md:px-8 md:py-28">
          <div className="grid items-end gap-10 md:grid-cols-2">
            <h2 className="font-display tracking-tighter" style={{ fontWeight: 700, fontSize: 'clamp(2.5rem, 7vw, 6rem)', lineHeight: 0.92 }}>
              Sourcing, <em className="giwahs-gold-emphasis" style={{ fontStyle: 'italic' }}>reimagined.</em>
            </h2>
            <div className="flex flex-col items-start gap-4 md:items-end">
              <Link to="/factories" data-testid="why-cta-browse" className="btn-giwahs btn-giwahs-outline-gold inline-flex items-center gap-2">
                Browse the directory <ArrowUpRight size={14} strokeWidth={1.5} />
              </Link>
              <Link to="/for-suppliers" data-testid="why-cta-supplier" className="giwahs-view-link label-overline">
                Are you a factory? Apply →
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default WhyGiwahs;
